import { useGLTF, Environment } from '@react-three/drei'
import { useEffect, useRef, useState } from 'react'
import emitter from '../helpers/MittEmitter'
import * as THREE from 'three'
import { SceneChild, LetterSentStatusEnum, BoundingBoxesMap } from '../types/portfolio_types'
import Button from './Button'
import MailmanCat from './MailmanCat'
import { interactable_world_objects } from '../interactable_data/world_data'
import { interactable_mailman_cat_objects } from '../interactable_data/mailman_cat_data'
import {
  pickedApplesAtom,
  floorLoadedAtom,
  boundingBoxesMapAtom,
  showHelpersAtom,
  justPickedApplesAtom,
} from '../store/store'
import { getAppleData, getLetterSentStatus } from '../store/localstorageData'
import { useAtom } from 'jotai'
import { useFrame, useThree } from '@react-three/fiber'
import AppleSparkles from './AppleSparkles'
import { deepDispose } from '../helpers/deepDispose'
import { removeAllHelpersFromScene } from '../helpers/helperFunctions'
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js'

const objects_with_custom_components = ['apple', 'red_button', 'button_base', 'mailman_cat_rig']
const exclusive_animations = [
  'jump_spring_animation',
  'mailman_cat_happy',
  'mailman_cat_idle',
  'mailman_cat_walk',
]

const animation_speeds: { [key: string]: number } = {
  'Pine tree animation': 0.7,
  'Balls tree armature left animation': 0.7,
  'Balls tree armature right animation': 0.7,
  'Balls tree armature top animation': 0.7,
  'cherry blossom leaves animation': 0.7,
  pink_flower_animation: 0.7,
  red_flower_animation: 0.7,
  sunflower_animation: 0.7,
  es_flag_animation: 0.7,
}

const Forest = () => {
  // Vite goes into the public folder by default
  const model = useGLTF('/models/portfolio_forest/portfolio_forest2.glb')
  const [sceneChildren, setSceneChildren] = useState<SceneChild[]>([])
  const [sceneClone, setSceneClone] = useState<THREE.Object3D<THREE.Object3DEventMap>>()
  const [mixer, setMixer] = useState<THREE.AnimationMixer>()
  const [pickedApples, setPickedApples] = useAtom(pickedApplesAtom)
  const [floorLoaded, setFloorLoaded] = useAtom(floorLoadedAtom)
  const [justPickedApples] = useAtom(justPickedApplesAtom)
  const letter_sent_status = useRef(getLetterSentStatus())
  const [bbMap, setBbMap] = useAtom(boundingBoxesMapAtom)
  const [showHelpers] = useAtom(showHelpersAtom)

  const three = useThree()

  // 1) CLONE THE SCENE SO IT DOESN'T GO AWAY WHEN WE UNMOUNT
  useEffect(() => {
    if (sceneClone) return
    // SET MIN FILTER IN THE TEXTURES THAT REQUIRE IT
    const forest_material = model.materials.forest_material as THREE.MeshStandardMaterial
    forest_material.map!.minFilter = THREE.LinearFilter

    const clonedScene = SkeletonUtils.clone(model.scene)
    clonedScene.animations = model.animations

    setSceneClone(clonedScene)
    setMixer(new THREE.AnimationMixer(clonedScene))
  }, [model, sceneClone])

  // 2) CREATE THE BOUNDING BOXES MAP + HELPERS FOR DEV MODE
  useEffect(() => {
    if (!sceneClone) return

    const boundingBoxesMap: BoundingBoxesMap = {}
    const objects_without_bodies_lax = [
      'sunflower',
      'pink_flowers',
      'red_flower',
      'cherry_blossom_tree_leaves',
      'big_pine_tree_leaves',
      'leaves_ball_top',
      'house_brick',
      'house_branch',
      'house_head',
      'house_chimney',
      'house_window',
      'house_doorknob',
      'sign',
      'above',
      'shadows',
      'sandbox_sand',
      'sandbox_corners',
      'armature',
      'button_base',
    ]
    const objects_without_bodies_specific = [
      'spanish_flag',
      'venezuelan_flag',
      'slide',
      'balls_box',
      'apple010box',
    ]
    const receive_shadow_objects = [
      'floor',
      'sandbox_sand',
      'sandbox_corners',
      'slide',
      'jump_spring',
      'red_button',
      'button_base',
      'correos_mailbox',
      'house_base',
      'house_door',
      'house_doorknob',
    ]

    for (const child of sceneClone.children) {
      // Mark the objects that will receive Bianca's shadow
      if (receive_shadow_objects.find((objName) => child.name.toLowerCase() === objName)) {
        child.receiveShadow = true

        child.children.forEach((obj) => {
          obj.receiveShadow = true
        })
      }

      if (
        objects_without_bodies_lax.find((objName) => child.name.toLowerCase().includes(objName)) ||
        objects_without_bodies_specific.find((objName) => child.name.toLowerCase() === objName)
      ) {
        continue
      }

      const { name } = child

      // Without this, mailman_cat_rig's bbox does not render
      child.updateMatrixWorld(true)

      const bbox = new THREE.Box3().setFromObject(child)

      if (name === 'cherry_blossom_tree_trunk001') {
        bbox.min.x += 7
        bbox.max.x -= 5
        bbox.min.z += 1.8
      }

      if (name === 'cherry_blossom_tree_trunk002') {
        bbox.min.x += 7
        bbox.max.x -= 5
        bbox.min.z += 1.8
      }

      if (name === 'cherry_blossom_tree_trunk003') {
        bbox.min.x += 3
        bbox.max.x -= 4
        bbox.min.z += 8
        bbox.max.z -= 5
      }

      if (name.startsWith('pine_tree0')) {
        bbox.min.x += 0.5
        bbox.max.x -= 0.5
        bbox.min.z += 0.5
        bbox.max.z -= 0.5
      }

      if (name.startsWith('leaves_ball')) {
        bbox.min.x += 0.3
        bbox.max.x -= 0.3
        bbox.min.z += 0.3
        bbox.max.z -= 0.3
      }

      if (name.startsWith('balls_tree_trunk')) {
        bbox.min.x += 0.5
        bbox.max.x -= 0.5
        bbox.min.z += 0.5
        bbox.max.z -= 0.5
      }

      if (name === 'professional_projects_arrow') {
        bbox.min.y += 0.1
        bbox.min.x += 4.4
        bbox.max.x -= 3.5
      }

      if (name === 'personal_projects_arrow') {
        bbox.min.y -= 0.1
        bbox.min.x += 3.5
        bbox.max.x -= 4.4
      }

      if (name.startsWith('bush001')) {
        bbox.min.x += 1
        bbox.max.x -= 1
        bbox.min.z += 1.3
        bbox.max.z -= 1
      }

      if (name.startsWith('bush002')) {
        bbox.min.x += 0.3
        bbox.max.x -= 0.3
        bbox.min.z += 0.3
        bbox.max.z -= 0.3
      }

      if (name === 'balls_tree_stump002' || name === 'balls_tree_stump004') {
        bbox.min.x += 1
        bbox.max.x -= 1
        bbox.min.z += 1
        bbox.max.z -= 1
      }

      if (name === 'balls_tree_stump001') {
        bbox.min.x += 0.5
        bbox.max.x -= 0.5
        bbox.min.z += 0.5
        bbox.max.z -= 0.5
      }

      if (name === 'pine_tree_stump001' || name === 'pine_tree_stump003') {
        bbox.min.x += 0.4
        bbox.max.x -= 0.4
        bbox.min.z += 0.4
        bbox.max.z -= 0.4
      }

      if (name === 'trainingmode_tree_trunk') {
        bbox.min.x += 4
        bbox.max.x -= 1
        bbox.min.z += 3
        bbox.max.z -= 1.5
      }

      const big_tree_trunks_facing_forwards = [
        'paperchat_tree_trunk',
        'bird_in_boots_tree_trunk',
        'whoolso_tree_trunk',
      ]

      if (big_tree_trunks_facing_forwards.includes(name)) {
        bbox.min.x += 1
        bbox.max.x -= 1
        bbox.min.z += 2
      }

      if (name.startsWith('house_base')) {
        bbox.min.x += 0.3
        bbox.max.x -= 0.3
        bbox.min.z += 0.3
      }

      let type: 'sensor' | 'collision' = 'collision'
      let color = 'yellow'

      if (name.startsWith('sensor_') || name.includes('apple')) {
        type = 'sensor'
        color = 'crimson'
      }

      if (name.includes('apple')) {
        color = '#41b94b'
      }

      boundingBoxesMap[name] = { name, bbox, type }

      if (showHelpers) {
        const boxHelper = new THREE.Box3Helper(bbox, color)
        boxHelper.name = `helper_${name}`
        three.scene.add(boxHelper)
      }

      // INTERACTION SENSORS
      if (interactable_world_objects[name] && !interactable_world_objects[name].skip_setting_position) {
        interactable_world_objects[name].position = child.position
      }
      if (
        interactable_mailman_cat_objects[name] &&
        !interactable_mailman_cat_objects[name].skip_setting_position
      ) {
        interactable_mailman_cat_objects[name].position = child.position
      }
    }

    setBbMap(boundingBoxesMap)
  }, [sceneClone, setBbMap, three.scene, showHelpers])

  // 3) CLEAN UP WHEN UNMOUNTING
  // This effect is set up so that it only runs when unmounting
  // Don't add more dependencies that could trigger it in re-renders.
  useEffect(() => {
    return () => {
      removeAllHelpersFromScene(three.scene)
      setBbMap(null)
      setSceneClone(undefined)
      setSceneChildren([])
    }
  }, [setBbMap, three.scene])

  // 4) POPULATE SCENE CHILDREN
  useEffect(() => {
    if (!sceneChildren.length && sceneClone) {
      const processedSceneChildren = sceneClone.children.map((child) => {
        return {
          object: child,
          element: <primitive object={child} key={child.id} />,
          interactable: interactable_world_objects[child.name],
        }
      })

      setSceneChildren(processedSceneChildren)
    }

    // DISPOSE OBJECTS ON UNMOUNT
    // This one can't be added to #3 because it'd imply adding sceneChildren to its dependency
    // array, and that one is only for unmounting, not re-rendering.
    return () => {
      // uncomment when deploying, this line delays dev re-render a bit
      sceneChildren.forEach((child) => deepDispose(child.object))
    }
  }, [sceneChildren, sceneClone])

  // 5) KEEP CHILDREN IN SCENE: When children with bones are put into a <primitive> and rendered, they're removed from clonedScene.children
  // If you try to play an animation later, it will throw a "THREE.PropertyBinding: No target node found for track"
  // error because some bones referenced by the animation aren't present in the scene.

  // However, this happens only if you don't play an animation that uses those bones before the child is
  // removed from the scene, as we do in Bianca.tsx.

  // You can test this out by commenting this useEffect and adding handleAnimation('jump_spring_animation')
  // right before the emitter.on('play_animation') line, the animation should work.

  // The animation would play before any scene child <primitive> can be rendered in the scene,
  // that is, useEffect 4 and 6 run in the same render cycle. Then, subsequent plays would work fine.
  useEffect(() => {
    if (!sceneChildren.length || !sceneClone) return

    sceneChildren.forEach((sceneChild) => {
      const child_is_in_scene = sceneClone.children.find((child) => child.name === sceneChild.object.name)
      if (!child_is_in_scene) sceneClone.children.push(sceneChild.object)
    })
  }, [sceneChildren, sceneClone])

  // 6) PLAY ANIMATIONS & LISTEN FOR PLAY ANIMATION EVENT
  useEffect(() => {
    if (!mixer || !sceneClone) return

    sceneClone.animations
      .filter((clip) => !exclusive_animations.includes(clip.name))
      .forEach((clip) => {
        const animation = mixer.clipAction(clip)
        animation.timeScale = animation_speeds[clip.name]
        animation.play()
      })

    const handleAnimation = (animation_name: string) => {
      const clip = sceneClone.animations.find((clip) => clip.name === animation_name)
      if (!clip) return

      const animation = mixer.clipAction(clip)
      animation.loop = THREE.LoopOnce
      animation.reset().play()
    }

    emitter.on('play_animation', handleAnimation)

    return () => emitter.off('play_animation')
  }, [mixer, sceneClone])

  useEffect(() => {
    if (!pickedApples) {
      const apple_data = getAppleData()
      setPickedApples(apple_data)
    }

    // This event is emitted from Letter.tsx, right after succesfully sending it.
    emitter.on('just_paid_apples', () => {
      const apple_data = getAppleData()
      letter_sent_status.current = getLetterSentStatus()
      setPickedApples(apple_data)
    })

    return () => emitter.off('just_paid_apples')
  }, [pickedApples, setPickedApples])

  useEffect(() => {
    if (!pickedApples) return
    localStorage.setItem('apples', JSON.stringify(pickedApples))
  }, [pickedApples])

  useEffect(() => {
    if (!floorLoaded && sceneChildren.length && bbMap?.floor) {
      setFloorLoaded(true)
    }
  }, [bbMap, floorLoaded, sceneChildren, setFloorLoaded])

  useFrame((state, delta) => {
    const time = state.clock.getElapsedTime()
    const apple_spin_speed = 3

    if (mixer) mixer.update(delta)

    sceneChildren
      .filter((child) => child.object.name.includes('apple'))
      .forEach((apple) => {
        if (apple.object.name.includes('box')) return

        const rotation = new THREE.Quaternion()
        rotation.setFromEuler(new THREE.Euler(0, time * apple_spin_speed, 0))
        apple.object.setRotationFromQuaternion(rotation)
      })
  })

  const renderButton = () => {
    const red_button = sceneChildren.find((child) => child.object.name === 'red_button')
    const button_base = sceneChildren.find((child) => child.object.name === 'button_base')
    const balls_box = sceneChildren.find((child) => child.object.name === 'balls_box')
    const balls_box_lid = sceneChildren.find((child) => child.object.name === 'balls_box_lid')

    const box_apple = sceneChildren.find((child) => child.object.name === 'apple010box')

    if (!red_button || !button_base || !balls_box || !balls_box_lid || !box_apple) return

    return (
      <Button
        button_base={button_base}
        red_button={red_button}
        balls_box={balls_box}
        balls_box_lid={balls_box_lid}
        box_apple={box_apple}
      />
    )
  }

  const renderApples = () => {
    const apples = sceneChildren.filter((child) => child.object.name.includes('apple'))

    const getSparkles = (apple_name: string, position: THREE.Vector3) => {
      if (!justPickedApples || !justPickedApples[apple_name]) return null

      return <AppleSparkles position={position} />
    }

    return apples.map((apple) => {
      const must_render_apple =
        !pickedApples?.list[apple.object.name] && letter_sent_status.current !== LetterSentStatusEnum.success

      return (
        <group key={apple.object.id}>
          {must_render_apple ? apple.element : null}
          {getSparkles(apple.object.name, apple.object.position)}
        </group>
      )
    })
  }

  const renderScene = () => {
    // Meshes are rendered only if they are visible in the camera (this is basically what frustum culling means)
    // However, suddenly rendering some meshes causes a frame drop, it's better to always render them from the beginning
    const objects_without_frustum_culling: string[] = ['spanish_flag', 'venezuelan_flag']

    const filteredScene = sceneChildren.filter((obj) => {
      if (obj.object.name.startsWith('barrier_') || obj.object.name.startsWith('sensor_')) {
        return false
      }

      return !objects_with_custom_components.find((obj_name) => obj.object.name.includes(obj_name))
    })

    return filteredScene.map((child) => {
      if (objects_without_frustum_culling.includes(child.object.name)) {
        child.object.frustumCulled = false
      }

      return child.element
    })
  }

  const renderMailmanCat = () => {
    if (!bbMap || !mixer || !sceneClone) return

    const cat_rig = sceneChildren.find((child) => child.object.name === 'mailman_cat_rig')!

    return <MailmanCat mailman_cat_rig={cat_rig} mixer={mixer} sceneAnimations={sceneClone.animations} />
  }

  return (
    <>
      {renderScene()}
      {renderButton()}
      {renderApples()}
      {renderMailmanCat()}

      <Environment
        background
        resolution={1024}
        environmentIntensity={0.1}
        files={[
          '/environment_maps/forest_sky/forest_sky.png',
          '/environment_maps/forest_sky/forest_sky.png',
          '/environment_maps/forest_sky/forest_sky.png',
          '/environment_maps/forest_sky/forest_sky.png',
          '/environment_maps/forest_sky/forest_sky.png',
          '/environment_maps/forest_sky/forest_sky.png',
        ]}
      />
    </>
  )
}

export default Forest

useGLTF.preload('/models/portfolio_forest/portfolio_forest2.glb')
